import { useCallback, useState, useEffect } from 'react';
import { object, string, date, array, number } from 'yup';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormik } from 'formik';
import { FormModal } from './FormModal';
import {
  RadioInput,
  AutocompleteInput,
  TextInput,
  SelectInput,
} from '../inputs';
import {
  RenderBatchTemplateForm,
  generatePayload as generateFilters,
} from './BatchTemplateForm';
import {
  useSelector,
  useDispatch,
  warehousesSelectors,
  customersSelectors,
  systemSelectors,
  getBasicWarehouses,
  getCustomerBasicList,
  getCarrierList,
  getBatchTemplates,
} from '../../../state';

import {
  authGet,
  generateUrl,
  paths,
  Navigation,
  convertNullFieldsToEmptyString,
} from '../../../lib';
import { useNotify } from '../../../hooks';
import { IdNamePair } from '../../../types';

const initialState = {
  batchName: '',
  mode: 'template',
  templateId: '',
  customerID: '',
  countryIDs: [],
  stateIDs: [],
  warehouseID: '',
  carrierCode: '',
  carrierServiceCode: '',
  batchType: '',
  preferenceOverride: '',
  // quantity: '',
  orderCreatedStart: null,
  orderCreatedEnd: null,
};

interface BatchFilterFormProps {
  open: boolean;
  handleClose: () => void;
  editData?: any;
}

export function BatchFilterForm({
  open,
  handleClose,
  editData,
}: BatchFilterFormProps) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const notify = useNotify();

  const [state, setState] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [carriers, setCarriers] = useState<Array<IdNamePair>>([]);
  const [servicesList, setServicesList] = useState<Array<IdNamePair>>([]);
  const batchTemplates = useSelector(systemSelectors.batchTemplates);

  const countries = useSelector(systemSelectors.countries);
  const batchTypes = useSelector(systemSelectors.orderBatchTypes);
  const shippingSelectionPreferenceTypes = useSelector(
    systemSelectors.shippingSelectionPreferenceTypes,
  );
  const customers = useSelector(customersSelectors.basicList);
  const states = useSelector(systemSelectors.states);
  const warehouses = useSelector(warehousesSelectors.basicList);

  const fetchServices = useCallback(
    async (carrierCode) => {
      if (carrierCode) {
        const { data } = await authGet([
          '/carriers/services/basic',
          { carrierCode },
        ]);
        if (data) {
          setServicesList(data);
        }
      }
    },
    [setServicesList],
  );

  useEffect(() => {
    (async function () {
      await dispatch(getBasicWarehouses());
      await dispatch(getCustomerBasicList());
      await dispatch(getBatchTemplates());

      (async function () {
        const { data } = await dispatch(
          getCarrierList({ includeManualCarriers: false }),
        );
        if (data) {
          const filtered = data.filter((d) => d.id !== 'UNKNOWN');
          setCarriers(filtered);
        }
      })();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function () {
      if (editData) {
        const { stateIDs, countryIDs } = editData;
        setState((cur) => ({
          ...cur,
          ...convertNullFieldsToEmptyString(editData),
          countryIDs: typeof countryIDs == 'string' ? [countryIDs] : countryIDs,
          stateIDs: typeof stateIDs == 'string' ? [stateIDs] : stateIDs,
          mode: 'custom',
        }));
        if (editData.carrierCode) {
          await fetchServices(editData.carrierCode);
        }
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitting(true);
      const payload = await generatePayload({
        values,
        notify,
        warehouses,
        customers,
        carrierServices: servicesList,
        carriers,
        countries,
        states,
      });
      setSubmitting(false);
      // if (editData) {
      //   refresh();
      // } else {
      Navigation.redirect(generateUrl(paths.BATCH_ORDERS, undefined, payload));
      // }
      handleClose();
    },
    [
      carriers,
      countries,
      customers,
      handleClose,
      notify,
      servicesList,
      states,
      warehouses,
    ],
  );

  const formik = useFormik({
    initialValues: { ...initialState, ...state },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  return (
    <FormModal
      open={open}
      handleClose={handleClose}
      title={<span className={classes.title}>Create a batch</span>}
      callback={formik.handleSubmit}
      btnText='CONTINUE'
      typeSubmit
      paperProps={{ style: { width: 720 } }}
      submitting={submitting}
    >
      <Grid container spacing={5} rowSpacing={2} className={classes.layout}>
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: 6,
          }}
        >
          <RadioInput
            formikProps={formik}
            name='mode'
            radiosRow
            size='small'
            title='Batch filters'
            radios={[
              { label: 'Template', value: 'template' },
              { label: 'Custom', value: 'custom' },
            ]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={5} rowSpacing={2} className={classes.layout}>
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: 6,
          }}
        >
          <SelectInput
            name='batchType'
            label='Batch type'
            formikProps={formik}
            options={batchTypes}
            className={classes.input}
          />
        </Grid>
        {formik.values.batchType === 'RateShop' && (
          <Grid
            style={{ paddingBottom: 0 }}
            size={{
              sm: 6,
            }}
          >
            <SelectInput
              name='preferenceOverride'
              label='Shipping preference override'
              formikProps={formik}
              options={shippingSelectionPreferenceTypes}
              className={classes.input}
            />
          </Grid>
        )}
        <Grid
          style={{ paddingBottom: 0 }}
          size={{
            sm: formik.values.mode === 'custom' ? 6 : 12,
          }}
        >
          <TextInput
            name='batchName'
            label='Batch Name'
            formikProps={formik}
            className={classes.input}
          />
        </Grid>
        {formik.values.mode === 'custom' && (
          <RenderBatchTemplateForm
            carriers={carriers}
            countries={countries}
            customers={customers}
            formik={formik}
            fetchServices={fetchServices}
            servicesList={servicesList}
            states={states}
            warehouses={warehouses}
          />
        )}
        {formik.values.mode === 'template' && (
          <Grid
            size={{
              sm: 12,
            }}
          >
            <AutocompleteInput
              formikProps={formik}
              textFieldProps={{
                name: 'templateId',
                label: 'Select template',
                className: classes.input,
                required: true,
              }}
              autocompleteProps={{
                options: batchTemplates,
              }}
            />
          </Grid>
        )}
      </Grid>
    </FormModal>
  );
}

const useStyles = makeStyles()((theme) => ({
  layout: {
    padding: '0 16px 16px 16px',
  },
  title: {
    fontFamily: 'Montserrat',
    fontSize: 20,
    fontWeight: 600,
    padding: '24px 16px',
  },
  input: {
    // marginBottom: 16,
    width: '100%',
  },
}));

const schema = object().shape({
  batchName: string().nullable(),
  batchType: string().required('Required'),
  customerID: string().when('mode', {
    is: 'custom',
    then: string().required('Required'),
  }),
  warehouseID: string().when('mode', {
    is: 'custom',
    then: string().required('Required'),
  }),
  templateId: string()
    .nullable()
    .when('mode', {
      is: 'template',
      then: string().nullable().required('Required'),
    }),
  orderCreatedStart: date()
    .typeError('Invalid Date')
    .nullable()
    .test(
      'both-or-none',
      'Both start and end dates are required',
      function (value) {
        const { orderCreatedEnd } = this.parent;
        if ((value && !orderCreatedEnd) || (!value && orderCreatedEnd)) {
          return false;
        }
        return true;
      },
    ),
  orderCreatedEnd: date()
    .typeError('Invalid Date')
    .nullable()
    .test(
      'both-or-none',
      'Both start and end dates are required',
      function (value) {
        const { orderCreatedStart } = this.parent;
        if ((value && !orderCreatedStart) || (!value && orderCreatedStart)) {
          return false;
        }
        return true;
      },
    ),
  stateIDs: array()
    .of(number().integer('Please enter a valid integer'))
    .nullable(),
  countryIDs: array()
    .of(number().integer('Please enter a valid integer'))
    .nullable(),
});

async function generatePayload({
  values,
  notify,
  customers,
  warehouses,
  carriers,
  carrierServices,
  countries,
  states,
}) {
  const { mode, batchName, batchType, preferenceOverride } = values;

  if (mode === 'template') {
    const { data, error } = await authGet(
      `/batches/batch-template/${values.templateId}`,
    );
    if (error) {
      return notify(error.message, 'error');
    } else if (data) {
      const { filtersResponse: { filters, filtersDisplay } = {} } = data;
      return {
        ...filters,
        ...filtersDisplay,
        batchName,
        batchType,
        preferenceOverride,
      };
    }
  } else {
    const { filters, filtersDisplay } = generateFilters({
      values,
      customers,
      warehouses,
      carriers,
      carrierServices,
      countries,
      states,
    });
    return {
      ...filters,
      ...filtersDisplay,
      batchName,
      batchType,
      preferenceOverride,
    };
  }
}
